import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  VolunteerActivism as OfferingIcon,
  AccountBalance as AccountBalanceIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { esES } from '@mui/x-data-grid/locales';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { donationService } from '../../services/donationService';
import { exportToExcel } from '../../utils/excelExport';
import dayjs from 'dayjs';
import { es } from 'date-fns/locale';

const OfferingList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [offerings, setOfferings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({
    total_amount: 0,
    confirmed_amount: 0,
    pending_amount: 0,
    total_count: 0,
    confirmed_count: 0,
    pending_count: 0
  });
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  
  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: '',
    search: ''
  });

  const formatCurrency = (amount: number, currency: string = 'RUB') => {
    const value = amount || 0;
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₽';
    return `${symbol} ${value.toLocaleString()}`;
};
  
  const [selectedOffering, setSelectedOffering] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    loadOfferings();
  }, [paginationModel, filters]);

  const loadOfferings = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...filters
      };
      
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await donationService.getAll(params);
      
      setOfferings(response.data.donations?.data || []);
      setTotal(response.data.donations?.total || 0);
      setTotals(response.data.totals || {});
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar ofrendas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedOffering) return;
    
    try {
      await donationService.delete(selectedOffering.id);
      loadOfferings();
      setDeleteDialogOpen(false);
      setSelectedOffering(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar ofrenda');
    }
  };

  const handleConfirm = async () => {
    if (!selectedOffering) return;
    
    try {
      await donationService.confirm(selectedOffering.id);
      loadOfferings();
      setConfirmDialogOpen(false);
      setSelectedOffering(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al confirmar ofrenda');
    }
  };

  const handleExport = () => {
    const data = offerings.map(o => ({
      'ID': o.id,
      'Donante': o.donor_name,
      'Monto': o.formatted_amount,
      'Fecha': dayjs(o.offering_date).format('DD/MM/YYYY'),
      'Estado': o.status === 'confirmed' ? 'Confirmado' : 
               o.status === 'pending' ? 'Pendiente' : 'Cancelado',
      'Referencia': o.bank_reference || '',
      'Descripción': o.description || '',
      'Usado para Mensualidad': o.used_for_tuition ? 'Sí' : 'No'
    }));
    
    exportToExcel(data, 'ofrendas');
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Chip icon={<CheckCircleIcon />} label="Confirmado" color="success" size="small" />;
      case 'pending':
        return <Chip icon={<ErrorIcon />} label="Pendiente" color="warning" size="small" />;
      case 'cancelled':
        return <Chip icon={<WarningIcon />} label="Cancelado" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'donor',
      headerName: 'Donante',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'secondary.main' }}>
            <OfferingIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.donor_name}
            </Typography>
            {params.row.parent && (
              <Typography variant="caption" color="text.secondary">
                Padre registrado
              </Typography>
            )}
          </Box>
        </Box>
      )
    },
    {
      field: 'amount',
      headerName: 'Monto',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="success.main">
          {formatCurrency(params.row.amount, params.row.currency)}
        </Typography>
      )
    },
    {
      field: 'offering_date',
      headerName: 'Fecha',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {dayjs(params.row.offering_date).format('DD/MM/YYYY')}
        </Typography>
      )
    },
    {
        field: 'donation_type',
        headerName: 'Método',
        width: 150,
        renderCell: (params) => {
            const typeMap: Record<string, string> = {
            cash: 'Efectivo',
            bank_transfer: 'Transferencia',
            check: 'Cheque',
            other: 'Otro'
            };
            return (
            <Chip 
                label={typeMap[params.value] || params.value} 
                size="small" 
                variant="outlined"
            />
            );
        }
    },
    {
      field: 'central_account',
      headerName: 'Cuenta',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <AccountBalanceIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.row.central_account?.name}
          </Typography>
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'used_for_tuition',
      headerName: 'Uso',
      width: 100,
      renderCell: (params) => (
        params.row.used_for_tuition ? (
          <Tooltip title={`Usado para pagar mensualidad de ${params.row.student?.first_name} ${params.row.student?.last_name}`}>
            <Chip icon={<SchoolIcon />} label="Usado" color="info" size="small" />
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary">Disponible</Typography>
        )
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/offerings/${params.row.id}`);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => navigate(`/offerings/${params.row.id}/edit`)}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'pending' && (
            <Tooltip title="Confirmar ofrenda">
              <IconButton
                size="small"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOffering(params.row);
                  setConfirmDialogOpen(true);
                }}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOffering(params.row);
                setDeleteDialogOpen(true);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Ofrendas y Donaciones
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ mr: 1 }}
          >
            Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/offerings/new')}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Nueva Ofrenda
          </Button>
        </Box>
      </Box>

      {/* Tarjetas de resumen */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="body2">Total Donado</Typography>
              <Typography variant="h4">
                ₽{totals.total_amount.toLocaleString()}
              </Typography>
              <Typography variant="caption">{totals.total_count} donaciones</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Typography variant="body2">Confirmadas</Typography>
              <Typography variant="h4">
                ₽{(totals?.confirmed_amount || 0).toLocaleString()}
              </Typography>
              <Typography variant="caption">{totals.confirmed_count} donaciones</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Typography variant="body2">Pendientes</Typography>
              <Typography variant="h4">
                ₽{totals.pending_amount.toLocaleString()}
              </Typography>
              <Typography variant="caption">{totals.pending_count} donaciones</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Promedio</Typography>
              <Typography variant="h4">
                ₽{(totals.total_amount / (totals.total_count || 1)).toFixed(0)}
              </Typography>
              <Typography variant="caption">por donación</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar donante..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Fecha Inicio"
              InputLabelProps={{ shrink: true }}
              value={filters.start_date}
              onChange={(e) => setFilters({...filters, start_date: e.target.value})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Fecha Fin"
              InputLabelProps={{ shrink: true }}
              value={filters.end_date}
              onChange={(e) => setFilters({...filters, end_date: e.target.value})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.status}
                label="Estado"
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="confirmed">Confirmado</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={() => loadOfferings()}
                fullWidth
              >
                Filtrar
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({ status: '', start_date: '', end_date: '', search: '' });
                }}
              >
                <RefreshIcon />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={offerings}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(params) => navigate(`/offerings/${params.id}`)}
          disableRowSelectionOnClick
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de eliminar esta ofrenda?
          </Typography>
          {selectedOffering && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Donante:</strong> {selectedOffering.donor_name}
              </Typography>
              <Typography variant="body2">
                <strong>Monto:</strong> {selectedOffering.formatted_amount}
              </Typography>
              <Typography variant="body2">
                <strong>Fecha:</strong> {dayjs(selectedOffering.offering_date).format('DD/MM/YYYY')}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmar Ofrenda</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Confirmar esta ofrenda? Al confirmarla, se registrará en el balance general.
          </Typography>
          {selectedOffering && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Donante:</strong> {selectedOffering.donor_name}
              </Typography>
              <Typography variant="body2">
                <strong>Monto:</strong> {selectedOffering.formatted_amount}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirm} color="success" variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfferingList;